import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const scrollIds = ['scrollWrapperRef', 'pageScrollWrapperRef', 'knowledgeBaseContainer'];
type Position = { left: number; top: number };
type Snapshot = Record<string, Position>;
const positions = new Map<string, Snapshot>();

const readPositions = (): Snapshot => {
	const snapshot: Snapshot = { window: { left: window.scrollX, top: window.scrollY } };
	scrollIds.forEach((id) => {
		const element = document.getElementById(id);
		if (element) snapshot[id] = { left: element.scrollLeft, top: element.scrollTop };
	});
	return snapshot;
};

export const HistoryScrollRestoration = () => {
	const location = useLocation();
	const navigationType = useNavigationType();
	const currentKey = useRef(location.key);
	const currentPath = useRef(location.pathname);
	const restoring = useRef(false);

	useEffect(() => {
		const previous = window.history.scrollRestoration;
		window.history.scrollRestoration = 'manual';
		const save = () => {
			if (!restoring.current) {
				positions.set(currentKey.current, readPositions());
				if (positions.size > 100) positions.delete(positions.keys().next().value!);
			}
		};
		// Capture before link handlers can reset the outgoing page's scroll.
		document.addEventListener('click', save, true);
		document.addEventListener('scroll', save, true);
		return () => {
			window.history.scrollRestoration = previous;
			document.removeEventListener('click', save, true);
			document.removeEventListener('scroll', save, true);
		};
	}, []);

	useLayoutEffect(() => {
		const changedPath = currentPath.current !== location.pathname;
		const previousPosition = positions.get(currentKey.current);
		currentKey.current = location.key;
		currentPath.current = location.pathname;
		let saved = navigationType === 'POP' ? positions.get(location.key) : undefined;
		if (navigationType !== 'POP' && location.state?.preserveScroll) {
			saved = previousPosition || readPositions();
			positions.set(location.key, saved);
		}
		if (!saved && !changedPath) return undefined;
		const target = saved || Object.fromEntries(['window', ...scrollIds].map((id) => [id, { left: 0, top: 0 }]));
		restoring.current = true;
		let timer: ReturnType<typeof setTimeout>;
		let stopped = false;
		const stop = () => {
			stopped = true;
			clearTimeout(timer);
			restoring.current = false;
		};
		const started = Date.now();
		const restore = () => {
			if (stopped) return;
			Object.entries(target).forEach(([id, position]) => {
				if (id === 'window') window.scrollTo({ ...position, behavior: 'instant' });
				else document.getElementById(id)?.scrollTo({ ...position, behavior: 'instant' });
			});
			// Lazy screens and query results may initially be too short to restore the position.
			if (saved && Date.now() - started < 3000) timer = setTimeout(restore, 50);
			else stop();
		};
		timer = setTimeout(restore, 0);
		window.addEventListener('wheel', stop, { passive: true });
		window.addEventListener('touchstart', stop, { passive: true });
		window.addEventListener('pointerdown', stop);
		window.addEventListener('keydown', stop);
		return () => {
			stop();
			window.removeEventListener('wheel', stop);
			window.removeEventListener('touchstart', stop);
			window.removeEventListener('pointerdown', stop);
			window.removeEventListener('keydown', stop);
		};
	}, [location.key, location.pathname, location.state, navigationType]);
	return null;
};
